import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { ClipboardList, Plus, FileText, Search } from "lucide-react";

export const Route = createFileRoute("/utvardering")({
  head: () => ({
    meta: [
      { title: "Prepare-utvärdering — Inkubera" },
      { name: "description", content: "Utvärdering inför Prepare-fasen för prospects." },
    ],
  }),
  component: UtvarderingList,
});

const STATUS_OPTIONS = [
  "utvärdering",
  "godkänd",
  "avslagen",
  "parkerad",
] as const;

type Row = {
  id: string;
  case_name: string;
  org_number: string | null;
  contact_name: string | null;
  status: string;
  created_at: string;
  evaluation?: {
    id: string;
    recommendation: string | null;
    evaluation_date: string;
    performed_by_name: string | null;
  } | null;
};

function UtvarderingList() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newContact, setNewContact] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("alla");

  const load = async () => {
    setLoading(true);
    const { data: prospects } = await supabase
      .from("prospects")
      .select("id, case_name, org_number, contact_name, status, created_at")
      .order("created_at", { ascending: false });
    const { data: evals } = await supabase
      .from("prepare_evaluations")
      .select("id, prospect_id, recommendation, evaluation_date, performed_by_name");
    const evMap = new Map((evals ?? []).map((e) => [e.prospect_id, e]));
    setRows(
      (prospects ?? []).map((p) => {
        const e = evMap.get(p.id);
        return {
          ...p,
          evaluation: e
            ? {
                id: e.id,
                recommendation: e.recommendation,
                evaluation_date: e.evaluation_date,
                performed_by_name: e.performed_by_name,
              }
            : null,
        };
      }),
    );
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const handleCreate = async () => {
    if (!newName.trim() || !user) return;
    setCreating(true);
    const { data, error } = await supabase
      .from("prospects")
      .insert({
        case_name: newName.trim(),
        contact_name: newContact.trim() || null,
        created_by: user.id,
      })
      .select("id")
      .single();
    setCreating(false);
    if (error || !data) {
      alert(error?.message ?? "Kunde inte skapa case");
      return;
    }
    navigate({ to: "/utvardering/$id", params: { id: data.id } });
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFilter !== "alla" && r.status !== statusFilter) return false;
      if (!q) return true;
      return (
        r.case_name.toLowerCase().includes(q) ||
        (r.contact_name ?? "").toLowerCase().includes(q) ||
        (r.org_number ?? "").toLowerCase().includes(q) ||
        (r.evaluation?.performed_by_name ?? "").toLowerCase().includes(q)
      );
    });
  }, [rows, search, statusFilter]);

  const counts = useMemo(() => {
    const c: Record<string, number> = { alla: rows.length };
    for (const s of STATUS_OPTIONS) c[s] = 0;
    for (const r of rows) c[r.status] = (c[r.status] ?? 0) + 1;
    return c;
  }, [rows]);

  return (
    <AppShell>
      <div className="px-8 py-10 max-w-6xl">
        <div className="flex items-center gap-3 mb-2">
          <ClipboardList className="size-6 text-muted-foreground" />
          <h1 className="font-display text-3xl">Prepare-utvärderingar</h1>
        </div>
        <p className="text-sm text-muted-foreground mb-8 max-w-2xl">
          Digital utvärderingsmall för affärscoacher. Skapa ett case, fyll i mallen och få en automatisk
          rekommendation baserat på Inkuberas antagningskriterier.
        </p>

        <div className="rounded-2xl border border-border bg-card p-5 mb-6">
          <div className="text-sm font-medium mb-3">Skapa nytt case</div>
          <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-3">
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Namn på case/företag"
              className="h-10 px-3 rounded-lg bg-secondary border border-transparent focus:border-ring focus:outline-none text-sm"
            />
            <input
              value={newContact}
              onChange={(e) => setNewContact(e.target.value)}
              placeholder="Kontaktperson (valfritt)"
              className="h-10 px-3 rounded-lg bg-secondary border border-transparent focus:border-ring focus:outline-none text-sm"
            />
            <button
              onClick={handleCreate}
              disabled={creating || !newName.trim()}
              className="h-10 px-4 rounded-lg bg-foreground text-background text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50"
            >
              <Plus className="size-4" />
              {creating ? "Skapar…" : "Skapa & öppna"}
            </button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-4">
          <div className="relative flex-1 min-w-64">
            <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Sök case, kontakt, org.nr eller utförare"
              className="w-full h-10 pl-9 pr-3 rounded-lg bg-secondary border border-transparent focus:border-ring focus:outline-none text-sm"
            />
          </div>
          <div className="flex gap-1 rounded-lg bg-secondary p-1">
            {(["alla", ...STATUS_OPTIONS] as const).map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`px-3 py-1.5 text-xs rounded-md capitalize ${
                  statusFilter === s ? "bg-background shadow" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {s} <span className="text-muted-foreground">({counts[s] ?? 0})</span>
              </button>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-secondary/50 text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="text-left p-3">Case</th>
                <th className="text-left p-3">Kontakt</th>
                <th className="text-left p-3">Status</th>
                <th className="text-left p-3">Rekommendation</th>
                <th className="text-left p-3">Utförd av</th>
                <th className="text-left p-3">Datum</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td className="p-4 text-muted-foreground" colSpan={7}>
                    Laddar…
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td className="p-4 text-muted-foreground" colSpan={7}>
                    {rows.length === 0 ? "Inga case ännu." : "Inga träffar för aktuellt filter."}
                  </td>
                </tr>
              )}
              {filtered.map((r) => (
                <tr key={r.id} className="border-t border-border hover:bg-secondary/30">
                  <td className="p-3">
                    <div className="font-medium">{r.case_name}</div>
                    {r.org_number && (
                      <div className="text-[11px] text-muted-foreground">{r.org_number}</div>
                    )}
                  </td>
                  <td className="p-3 text-muted-foreground">{r.contact_name ?? "—"}</td>
                  <td className="p-3">
                    <StatusPill status={r.status} />
                  </td>
                  <td className="p-3">
                    {r.evaluation?.recommendation ? (
                      <RecBadge text={r.evaluation.recommendation} />
                    ) : (
                      <span className="text-muted-foreground text-xs">Ej utvärderad</span>
                    )}
                  </td>
                  <td className="p-3 text-muted-foreground text-xs">
                    {r.evaluation?.performed_by_name ?? "—"}
                  </td>
                  <td className="p-3 text-muted-foreground text-xs">
                    {r.evaluation
                      ? new Date(r.evaluation.evaluation_date).toLocaleDateString("sv-SE")
                      : "—"}
                  </td>
                  <td className="p-3 text-right">
                    <Link
                      to="/utvardering/$id"
                      params={{ id: r.id }}
                      className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-secondary hover:bg-secondary/80"
                    >
                      <FileText className="size-3" />
                      Öppna
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AppShell>
  );
}

function StatusPill({ status }: { status: string }) {
  const cls =
    status === "godkänd"
      ? "bg-green-500/15 text-green-700 dark:text-green-300"
      : status === "avslagen"
        ? "bg-red-500/15 text-red-700 dark:text-red-300"
        : status === "parkerad"
          ? "bg-muted text-muted-foreground"
          : "bg-amber-500/15 text-amber-700 dark:text-amber-300";
  return (
    <span className={`text-xs px-2.5 py-1 rounded-full font-medium capitalize ${cls}`}>{status}</span>
  );
}

function RecBadge({ text }: { text: string }) {
  const cls =
    text === "Rekommenderas"
      ? "bg-green-500/15 text-green-700 dark:text-green-300"
      : text === "Rekommenderas med åtgärder"
        ? "bg-amber-500/15 text-amber-700 dark:text-amber-300"
        : text === "Ej rekommenderas"
          ? "bg-red-500/15 text-red-700 dark:text-red-300"
          : "bg-muted text-muted-foreground";
  return <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${cls}`}>{text}</span>;
}
